<template>
    <ui-slider
        ref="slider"
        v-model="activeItem"
        class="private-lifestyle-item-slider"
        :slides-per-view="{ desktop: 3, mobile: 1 }"
        center
        pagination-mobile
        dark
        loop
        no-shadow
        no-arrows
        :pagination-color="['#2F2F31', '#FFA360']"
        :speed="700"
        @visible="visibleSlidesRaw = $event"
        @slides-progress="slidesZoomRaw = $event"
        @looped-slides="loopedSlides = $event"
    >
        <template
            v-for="(slide, i) in slides"
            v-slot:[`slide-${i}`]
        >
            <div
                :key="i"
                :data-slide-i="i"
                class="private-lifestyle-item-slider__slide"
            >
                <div
                    class="private-lifestyle-item-slider__slide-bg"
                    :data-bg-i="i"
                    :style="{
                        backgroundImage: `url(${slide.bg})`,
                    }"
                />
                <div
                    class="private-lifestyle-item-slider__slide-img"
                    :data-img-i="i"
                    :style="{
                        backgroundImage: `url(${slide.image})`,
                    }"
                    @click="activeItem = i"
                />
                <div class="private-lifestyle-item-slider__slide-text">
                    <div
                        class="text-align-left-center h4 _mb-16"
                        :data-title-i="i"

                    >{{ slide.title }}</div>
                    <div
                        class="text-align-left-center p1"
                        :data-description-i="i"
                    >{{ slide.description }}</div>
                    <a
                        v-if="slide.btnText"
                        :href="slide.btnLink"
                        class="button button_black button_size-sm _mt-16"
                        :data-btn-i="i"
                    >{{ slide.btnText }}</a>
                </div>
            </div>
        </template>
    </ui-slider>
</template>
<script>
import { useMutationObserver } from '@vueuse/core';
import fastdom from 'fastdom';
import { shallowRef, ref } from 'vue';

export default {
    name: 'PrivateLifestyleItemSlider',
    props: {
        value: Number,
        slides: {
            type: Array,
            default: () => [],
        },
    },
    setup() {
        const slidesAllElements = shallowRef([]);
        const bgAllElements = shallowRef([]);
        const imgAllElements = shallowRef([]);
        const titleAllElements = shallowRef([]);
        const descriptionAllElements = shallowRef([]);
        const btnAllElements = shallowRef([]);
        const slider = ref(null);

        useMutationObserver(slider, () => {
            const el = slider.value?.$el;
            if (!el) return;

            slidesAllElements.value = el?.querySelectorAll(':scope [data-slide-i]') ?? [];
            bgAllElements.value = el?.querySelectorAll(':scope [data-bg-i]') ?? [];
            imgAllElements.value = el?.querySelectorAll(':scope [data-img-i]') ?? [];
            titleAllElements.value = el?.querySelectorAll(':scope [data-title-i]') ?? [];
            descriptionAllElements.value = el?.querySelectorAll(':scope [data-description-i]') ?? [];
            btnAllElements.value = el?.querySelectorAll(':scope [data-btn-i]') ?? [];
        }, { attributes: false, childList: true, subtree: true })

        const next = () => slider.value?.next();
        const prev = () => slider.value?.prev();

        return {
            slidesAllElements,
            bgAllElements,
            imgAllElements,
            titleAllElements,
            descriptionAllElements,
            btnAllElements,
            slider,
            next,
            prev,
        };
    },
    data() {
        return {
            visibleSlidesRaw: [],
            slidesZoomRaw: [],
            activeItem: 0,
            slideOpacities: [],
            slideGrayscales: [],
            bgTransforms: [],
            bgSizes: [],
            bgPositions: [],
            imgSizes: [],
            titleOpacities: [],
            textOpacities: [],
            textTransforms: [],
            loopedSlides: 0,
        };
    },
    computed: {
        slidesZoomObj() {
            const obj = {};
            this.visibleSlides.forEach((key, i) => {
                const keyRaw = this.visibleSlidesRaw[i];
                obj[key] = this.slidesZoomRaw[keyRaw];
            });

            return obj;
        },
        visibleSlides() {
            return this.visibleSlidesRaw.map((i) => (i + this.slides.length - this.loopedSlides) % this.slides.length );
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(val) {
                this.activeItem = val;
            },
        },
        activeItem(val) {
            if (val === this.value) return;

            this.$emit('input', val);
        },
        slidesZoomObj: {
            immediate: true,
            deep: true,
            handler(val) {
                Object.entries(val).forEach(([key, zoomRaw]) => {
                    const i = Number(key);
                    const zoom = Math.max(Math.min(zoomRaw, 1), -1); // -1...0...1
                    const absZoom = Math.abs(zoom); // 1...0...1
                    const zzOne = Math.max(zoom, 0); // 0...0...1
                    const absZoomInv = 1 - absZoom; // 0...1...0

                    this.$set(this.slideOpacities, i, Math.min(zoom + 1, 1));  // 0...1...1
                    this.$set(this.slideGrayscales, i, `grayscale(${zzOne})`); // 0...0...1
                    this.$set(this.bgTransforms, i, `translateX(${5 * absZoom}%)`); // 1...0...1
                    this.$set(this.imgSizes, i, `${74 + 26 * absZoomInv}% auto`); // 0...1...0
                    this.$set(this.titleOpacities, i, Math.min(1.5 - zoom, 1)); // 1...1...0.5
                    this.$set(this.textOpacities, i, Math.min(1 - zoom, 1)); // 1...1...0
                    this.$set(this.textTransforms, i, `translateY(${zzOne * 30}%)`); // 0...0...1
                    this.$set(this.bgSizes, i, `${45 + 55 * absZoomInv}% auto`); // 0...1...0
                    this.$set(this.bgPositions, i, zoom < 0 ? `${absZoomInv*50+50}%` : `50%`); // 0...1...0
                });

                // set styles
                this.slidesAllElements.forEach((el) => {
                    const i = el.dataset.slideI;
                    fastdom.mutate(() => {
                        el.style.opacity = this.slideOpacities[i];
                        el.style.filter = this.slideGrayscales[i];
                    });
                });

                this.bgAllElements.forEach((el) => {
                    const i = el.dataset.bgI;
                    fastdom.mutate(() => {
                        el.style.backgroundSize = this.bgSizes[i];
                        el.style.backgroundPosition = this.bgPositions[i];
                        el.style.transform = this.bgTransforms[i];
                    });
                });

                this.imgAllElements.forEach((el) => {
                    const i = el.dataset.imgI;
                    fastdom.mutate(() => {
                        el.style.backgroundSize = this.imgSizes[i];
                    });
                });

                this.titleAllElements.forEach((el) => {
                    const i = el.dataset.titleI;
                    fastdom.mutate(() => {
                        el.style.opacity = this.titleOpacities[i];
                    });
                });

                this.descriptionAllElements.forEach((el) => {
                    const i = el.dataset.descriptionI;
                    fastdom.mutate(() => {
                        el.style.opacity = this.textOpacities[i];
                        el.style.transform = this.textTransforms[i];
                    });
                });

                this.btnAllElements.forEach((el) => {
                    const i = el.dataset.btnI;
                    fastdom.mutate(() => {
                        el.style.opacity = this.textOpacities[i];
                        el.style.transform = this.textTransforms[i];
                    });
                });
            },
        },
    },
};
</script>
<style lang="scss">
@import '../../assets/scss/base/variables';
@import '../../assets/scss/base/mixins';
@import '../../assets/scss/base/functions';

.private-lifestyle-item-slider {
    z-index: 0;
    &__slide {
        @include fluid-1920('width', 384px);
        @include mobile {
            width: 100%;
        }
        @include fluid-1920('margin-left', 84px, 0px);
        @include fluid-1920('margin-right', 55px, 0px);
        position: relative;
    }

    &__slide-img {
        user-select: none;
        cursor: pointer;
        @include fluid-1920('height', 670px, 360px);
        @include mobile {
            @include fluid-1920('width', 200px);
        }
        margin-left: auto;
        margin-right: auto;
        @include fluid-1920('margin-bottom', 64px, 50px);
        background-position: left bottom;
        background-size: 100% auto;
        background-repeat: no-repeat;
        position: relative;
    }

    &__slide-bg {
        user-select: none;
        pointer-events: none;
        position: absolute;
        z-index: -1;
        @include fluid-1920('top', 0px, 32px);
        @include fluid-1920('left', -504px, -70px);
        background-size: 100% auto;
        background-position: 50%;
        background-repeat: no-repeat;
        @include fluid-1920('height', 1082px, 500px);
        @include fluid-1920('width', 1152px, 540px);
    }

    &__slide-text {
        position: relative;
        @include fluid-1920('min-height', 348px, 140px);
        @include fluid-1920('padding-left', 16px);
        @include fluid-1920('padding-right', 16px);
        user-select: none;
    }

}
</style>
